<template>
	<div>
		<p>
			<campaigns-tooltip-label
				tooltip="If checked, successful campaign leads will be saved to the selected MailPoet list."
				label="Save lead to MailPoet?"
			>
				<common-toggle-input
					v-model="settingMailpoet"
					class="omapi-input"
					wrap-tag="span"
					on-text="Yes"
					off-text="No"
				/>
			</campaigns-tooltip-label>
		</p>
		<div v-if="settingMailpoet">
			<p>
				<campaigns-tooltip-label
					tooltip="All successful leads for the campaign will be added to this particular MailPoet list."
					label="Add lead to this MailPoet list:"
				>
					<select v-model="settingMpList" class="omapi-input omapi-input__select">
						<option
							v-for="option in mailPoetLists"
							:key="'mailPoetLists-' + option.value"
							:value="option.value"
						>
							{{ option.name }}
						</option>
					</select>
				</campaigns-tooltip-label>
			</p>
			<div v-if="hasFieldsToMap">
				<h3>Map the campaign fields to MailPoet custom fields</h3>
				<p>
					<campaigns-tooltip-label
						tooltip="If checked, unmapped fields will be automatically created in MailPoet using the field name set in the Campaign Builder."
						label="Auto create fields?"
					>
						<common-toggle-input
							v-model="settingMpAutoCreateFields"
							class="omapi-input"
							wrap-tag="span"
							on-text="Yes"
							off-text="No"
						/>
					</campaigns-tooltip-label>
				</p>
				<p v-for="(fieldLabel, fieldKey, index) in mappableFields" :key="index">
					<campaigns-tooltip-label
						:tooltip="`If you have a custom field for ${fieldLabel || fieldKey}, select the field here.`"
						:label="`Select the custom field for ${fieldLabel || fieldKey}:`"
					>
						<select
							class="omapi-input omapi-input__select"
							@change="handleFieldMappingChange($event, fieldKey)"
						>
							<option
								v-for="option in mailPoetFields"
								:key="`mailPoetFields-${option.value}`"
								:value="option.value"
								:selected="option.value === mappedFieldData[fieldKey]"
							>
								{{ option.name }}
							</option>
						</select>
					</campaigns-tooltip-label>
				</p>
			</div>
		</div>
	</div>
</template>

<script lang="js">
import { mapGetters } from 'vuex'
import { campaign } from '@/campaigns/mixins/campaign'
import { mapActions } from 'vuex'

export default  {
	mixins : [ campaign ],
	props  : {
		campaign : {
			type     : Object,
			required : true,
		},
	},
	data () {
		return {
			mappableFields: {},
		}
	},
	computed : {
		...mapGetters('wp', ['mailPoetLists', 'mailPoetFields']),

		hasFieldsToMap() {
			return Object.keys(this.mappableFields).length
		},

		mappedFieldData() {
			return this.settingMpMappedFields || {};
		},
	},
	watch: {
		settingMailpoet(val) {
			// When enabled and we haven't fetched yet, fetch the fields for mapping.
			if (val && !this.hasFieldsToMap) {
				this.fetchFieldsForMapping();
			}
		},
	},
	mounted() {
		// If MailPoet is enabled, fetch the fields for mapping.
		if (this.settingMailpoet) {
			this.fetchFieldsForMapping();
		}

		// Convert legacy phone field mapping to new mapping system if no existing mapping yet.
		if (this.settingMpPhone && !this.settingMpMappedFields['lead.phoneInput']) {
			this.setMappedField('lead.phoneInput', this.settingMpPhone);
		}
	},
	methods : {
		...mapActions('campaigns', [ 'fetchMpFieldMappingData' ]),

		fetchFieldsForMapping() {
			this.fetchMpFieldMappingData({ campaignId: this.id })
				.then((fieldsConfig) => {
					// Store the fields config as a meta property to the campaign.
					this.settingMpOptinFieldsConfig = fieldsConfig;

					this.setMappableFields(fieldsConfig);
				})
				.catch(() => {})
		},

		setMappableFields(fieldsConfig) {
			const mappableLeadFieldKeys = ['phoneInput', 'privacyText'];

			const mappableLeadFields = (fieldsConfig.lead || []).filter((field) => {
				// Ignore other fields.
				if (!mappableLeadFieldKeys.includes(field.name)) {
					return false;
				}

				// Map the privacyText only if it's a checkbox.
				if ('privacyText' === field.name && !field.checkbox) {
					return false;
				}

				return true;
			}).reduce((acc, field) => {
				// Prefix lead fields with 'lead.'.
				acc['lead.' + field.name] = field.label;

				return acc;
			}, {});

			// All meta fields are mappable.
			const mappableMetaFields = (fieldsConfig.meta || []).reduce((acc, field) => {
				// Prefix meta fields with 'meta.'.
				acc['meta.' + field.name] = field.label;

				return acc;
			}, {});

			const mappableTagFieldKeys = ['couponCode', 'couponLabel'];
			const mappableTagsFields = (fieldsConfig.tags || [])
				.filter((field) => mappableTagFieldKeys.includes(field.name))
				.reduce((acc, field) => {
					// Prefix tag fields with 'tag.'.
					acc['tags.' + field.name] = field.label;

					return acc;
				}, {});

			this.mappableFields = {
				...mappableLeadFields,
				...mappableMetaFields,
				...mappableTagsFields,
			};
		},

		handleFieldMappingChange(event, name) {
			this.setMappedField(name, event.target.value);
		},

		setMappedField(name, value) {
			// Create a copy of the current object
			let newSettingMpMappedFields = { ...this.settingMpMappedFields };

			if (!value) {
				delete newSettingMpMappedFields[name];
			} else {
				newSettingMpMappedFields[name] = value;
			}

			// Assign the new object to the property, triggering the wpSetting.set method
			this.settingMpMappedFields = newSettingMpMappedFields;
		}
	},
};
</script>
